/**
 * Shared portal calculation logic (mobile + saved-strategy preview).
 * Core simulation lives in portalSimulation.js (same engine as PortalPage).
 */
import { useState, useMemo, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ANNUAL_RATE_15, ANNUAL_RATE_35 } from "./loanConstants";
import { computePortalSchedule } from "./portalSimulation";

/** Slider units 0–50 (step 1) = 0.0%–5.0% per jaar; decimaal tarief = waarde/1000. */
export const PORTAL_GROWTH_SLIDER_MAX = 50;

export function sampleAtMonth(schedule, mi) {
  let before = null;
  let after = null;
  let beforeIdx = -1;
  for (let i = schedule.length - 1; i >= 0; i--) {
    if (schedule[i].m <= mi) {
      before = schedule[i];
      beforeIdx = i;
      break;
    }
  }
  if (beforeIdx >= 0 && beforeIdx < schedule.length - 1) after = schedule[beforeIdx + 1];
  if (!before && !after) return { balance: 0, interestCumulative: 0, investmentValue: 0 };
  if (!after) return { balance: before.balance, interestCumulative: before.interestCumulative, investmentValue: before.investmentValue ?? 0 };
  if (!before) return { balance: after.balance, interestCumulative: after.interestCumulative, investmentValue: after.investmentValue ?? 0 };
  const t = (mi - before.m) / Math.max(1, after.m - before.m);
  return {
    balance: before.balance + (after.balance - before.balance) * t,
    interestCumulative: before.interestCumulative + (after.interestCumulative - before.interestCumulative) * t,
    investmentValue: (before.investmentValue ?? 0) + ((after.investmentValue ?? 0) - (before.investmentValue ?? 0)) * t,
  };
}

export function usePortalCalculations(loadStrategy = null) {
  const location = useLocation();
  const navigate = useNavigate();

  const [totalDebt, setTotalDebt] = useState(23000);
  const [repaymentPlan, setRepaymentPlan] = useState("15");
  const [minPayment, setMinPayment] = useState(0);
  const [monthlyPayment, setMonthlyPayment] = useState(250);
  const [enableGrowth, setEnableGrowth] = useState(false);
  const [growthRate, setGrowthRateInternal] = useState(0);
  const setGrowthRate = useCallback((v) => {
    setGrowthRateInternal((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      const n = Number(next);
      if (!Number.isFinite(n)) return prev;
      return Math.min(PORTAL_GROWTH_SLIDER_MAX, Math.max(0, n));
    });
  }, []);
  const [monthlyInvestment, setMonthlyInvestment] = useState(0);
  const [delayYears, setDelayYears] = useState(0);
  const [startingInvestmentCapital, setStartingInvestmentCapital] = useState(0);
  const [enableSeparateGrowth, setEnableSeparateGrowth] = useState(false);
  const [investmentGrowthRate, setInvestmentGrowthRateInternal] = useState(0);
  const setInvestmentGrowthRate = useCallback((v) => {
    setInvestmentGrowthRateInternal((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      const n = Number(next);
      if (!Number.isFinite(n)) return prev;
      return Math.min(PORTAL_GROWTH_SLIDER_MAX, Math.max(0, n));
    });
  }, []);
  const [hasStartedRepaying, setHasStartedRepaying] = useState(false);
  const [yearsAlreadyRepaid, setYearsAlreadyRepaid] = useState(0);
  const [oneTimeRepayments, setOneTimeRepayments] = useState([{ amount: 0, year: 1 }]);
  const [enableCustomRates, setEnableCustomRates] = useState(false);
  const [customRates, setCustomRates] = useState([]);

  /** Geen aparte maanden meer: oude strategieën met delayMonths worden naar jaren geconverteerd. */
  const delayMonths = 0;

  const enableDelay = delayYears > 0;
  const enableInvestment = useMemo(
    () =>
      (Number(monthlyInvestment) || 0) > 0 || (Number(startingInvestmentCapital) || 0) > 0,
    [monthlyInvestment, startingInvestmentCapital]
  );

  // Load a saved strategy (mobile)
  useEffect(() => {
    if (loadStrategy && typeof loadStrategy === "object" && loadStrategy.id != null) {
      setTotalDebt(loadStrategy.totalDebt ?? 23000);
      setRepaymentPlan(String(loadStrategy.repaymentPlan ?? 15));
      setMinPayment(loadStrategy.minPayment ?? 0);
      setMonthlyPayment(loadStrategy.monthlyPayment ?? 250);
      setEnableGrowth(!!loadStrategy.enableGrowth);
      setGrowthRate(loadStrategy.growthRate ?? 0);
      setEnableSeparateGrowth(!!loadStrategy.enableSeparateGrowth);
      setInvestmentGrowthRate(loadStrategy.investmentGrowthRate ?? 0);
      setMonthlyInvestment(loadStrategy.monthlyInvestment ?? 0);
      const dy = Number(loadStrategy.delayYears) || 0;
      const dm = Number(loadStrategy.delayMonths) || 0;
      const totalDelayMonths = dy * 12 + dm;
      setDelayYears(
        totalDelayMonths > 0 ? Math.min(5, Math.ceil(totalDelayMonths / 12)) : 0
      );
      setStartingInvestmentCapital(loadStrategy.startingInvestmentCapital ?? 0);
      setHasStartedRepaying(!!loadStrategy.hasStartedRepaying);
      setYearsAlreadyRepaid(loadStrategy.yearsAlreadyRepaid ?? 0);
      const otr = loadStrategy.oneTimeRepayments;
      setOneTimeRepayments(Array.isArray(otr) && otr.length > 0 ? otr : [{ amount: 0, year: 1 }]);
      setEnableCustomRates(!!loadStrategy.enableCustomRates);
      setCustomRates(Array.isArray(loadStrategy.customRates) && loadStrategy.customRates.length > 0 ? loadStrategy.customRates : []);
    }
  }, [loadStrategy?.id]);

  // Prefill from signup
  useEffect(() => {
    if (location.state?.prefillData) {
      const { totalDebt: td, planYears } = location.state.prefillData;
      if (td != null) setTotalDebt(td);
      if (planYears != null) setRepaymentPlan(String(planYears));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const planYears = Number(repaymentPlan) || 15;
  const yearsAlready = hasStartedRepaying ? Math.min(Math.max(0, yearsAlreadyRepaid), planYears - 1) : 0;
  const effectivePlanYears = Math.max(1, planYears - yearsAlready);
  const totalTermYears = effectivePlanYears + delayYears;
  const annualRate = planYears === 15 ? ANNUAL_RATE_15 : ANNUAL_RATE_35;
  const monthlyRate = annualRate / 12;
  const defaultRatePct = planYears === 15 ? 2.21 : 2.57;
  const customRatesSorted = useMemo(() => {
    if (!enableCustomRates || !customRates?.length) return [];
    return [...customRates]
      .filter((r) => r != null && typeof r.year === "number" && typeof r.ratePct === "number")
      .sort((a, b) => a.year - b.year);
  }, [enableCustomRates, customRates]);
  const getMonthlyRateForMonth = useCallback(
    (monthIndex) => {
      if (customRatesSorted.length === 0) return monthlyRate;
      const y = monthIndex / 12;
      let seg = customRatesSorted[0];
      for (const s of customRatesSorted) {
        if (s.year <= y) seg = s;
        else break;
      }
      return (seg.ratePct / 100) / 12;
    },
    [customRatesSorted, monthlyRate]
  );
  const calculatedMinPayment = minPayment;
  const effectiveMinPayment = calculatedMinPayment;
  const constrainedMonthlyPayment = Math.max(monthlyPayment, effectiveMinPayment);

  const gr = growthRate / 1000;
  const igr = enableSeparateGrowth ? investmentGrowthRate / 1000 : growthRate / 1000;

  const result = useMemo(() => {
    return computePortalSchedule({
      totalDebt,
      constrainedMonthlyPayment,
      effectivePlanYears,
      totalTermYears,
      enableDelay,
      delayYears,
      delayMonths,
      enableGrowth,
      gr,
      igr,
      enableInvestment,
      monthlyInvestment,
      startingInvestmentCapital,
      oneTimeRepayments,
      getMonthlyRateForMonth,
    });
  }, [
    totalDebt,
    constrainedMonthlyPayment,
    planYears,
    effectivePlanYears,
    totalTermYears,
    getMonthlyRateForMonth,
    oneTimeRepayments,
    enableGrowth,
    gr,
    enableSeparateGrowth,
    igr,
    enableInvestment,
    monthlyInvestment,
    enableDelay,
    delayYears,
    delayMonths,
    startingInvestmentCapital,
  ]);

  const { schedule, payoffMonths, interestAtEnd, finalInvestmentValue, payoffByInvestment } = result;
  const payoffYears = Math.floor(payoffMonths / 12);
  const payoffRemainderMonths = Math.round(payoffMonths - payoffYears * 12);

  // Total cost = som van werkelijke maandelijkse aflossingen uit de schedule (incl. laatste maand ≤ betaling, 0/deel investering)
  const totalCost = useMemo(() => {
    if (schedule.length < 2) return 0;
    const fractionalMonth = payoffMonths - Math.floor(payoffMonths);
    return schedule.reduce((sum, row, i) => {
      const pay = row.loanPayment ?? 0;
      if (fractionalMonth > 0 && i === schedule.length - 1) return sum + pay * fractionalMonth;
      return sum + pay;
    }, 0);
  }, [schedule, payoffMonths]);

  // Total investment payments = som van werkelijke maandelijkse investeringen uit de schedule (laatste maand 0 of deel)
  const totalInvestmentPayments = useMemo(() => {
    if (!enableInvestment || (Number(monthlyInvestment) || 0) <= 0) return 0;
    const fractionalMonth = payoffMonths - Math.floor(payoffMonths);
    return schedule.reduce((sum, row, i) => {
      const contrib = row.investmentContribution ?? 0;
      if (fractionalMonth > 0 && i === schedule.length - 1) return sum + contrib * fractionalMonth;
      return sum + contrib;
    }, 0);
  }, [enableInvestment, monthlyInvestment, schedule, payoffMonths]);

  const totalOneTimeRepayments = useMemo(() => {
    const yearsApplied = Math.max(0, Math.floor(payoffMonths / 12));
    return (oneTimeRepayments || [])
      .filter((r) => r && (r.amount || 0) > 0)
      .reduce((sum, r) => sum + (r.year === 0 ? (Number(r.amount) || 0) * yearsApplied : (Number(r.amount) || 0)), 0);
  }, [oneTimeRepayments, payoffMonths]);
  const totalCostWithInvestments = totalCost + totalInvestmentPayments + totalOneTimeRepayments;

  // Chart data: one point per year (for simple use)
  // principal = balance at start of year (voor "Totale schuld" begin); interest/investment = cumulatief aan einde van jaar (voor "Totaal betaalde rente")
  // Geen jaren na aflossingsjaar: laatste jaar-index = floor((payoffMonths-1)/12)
  const chartData = useMemo(() => {
    const points = [];
    const maxY = Math.max(0, Math.floor((payoffMonths - 1) / 12));
    for (let y = 0; y <= maxY; y++) {
      const miStart = Math.min(y * 12, payoffMonths);
      const miEnd = Math.min((y + 1) * 12, payoffMonths);
      const sStart = sampleAtMonth(schedule, miStart);
      const sEnd = sampleAtMonth(schedule, miEnd);
      points.push({
        year: y,
        principal: sStart.balance,
        interest: sEnd.interestCumulative,
        investment: sEnd.investmentValue ?? 0,
      });
    }
    return points;
  }, [schedule, payoffMonths]);

  // Monthly chart data: same structure as PC yearlyData (one point per month) for exact same chart
  const chartDataMonthly = useMemo(() => {
    const data = [];
    const maxTermMonths = enableDelay ? totalTermYears * 12 : effectivePlanYears * 12;
    for (let month = 0; month <= maxTermMonths; month++) {
      if (month > payoffMonths && !(enableInvestment && payoffByInvestment)) break;
      const s = sampleAtMonth(schedule, month);
      let principalValue = s.balance;
      if (enableInvestment && payoffByInvestment) {
        if (month >= payoffMonths && s.balance === 0 && (s.investmentValue ?? 0) > 0) {
          principalValue = s.investmentValue ?? 0;
        }
      }
      let finalPrincipal = Math.max(0, principalValue);
      if (enableInvestment && payoffByInvestment && month >= payoffMonths && s.balance === 0) {
        finalPrincipal = s.investmentValue ?? 0;
      }
      data.push({
        year: month / 12,
        principal: finalPrincipal,
        interest: s.interestCumulative,
        investment: s.investmentValue ?? 0,
      });
      if (enableInvestment && payoffByInvestment && month >= payoffMonths) break;
      if (enableInvestment && !payoffByInvestment && (s.investmentValue ?? 0) >= s.balance && s.balance > 0) break;
    }
    // Add final payoff point if fractional (match PC)
    const payoffMonthsIsInteger = Math.abs(payoffMonths - Math.round(payoffMonths)) < 1e-9;
    const lastMonthInData = data.length > 0 ? data[data.length - 1].year * 12 : -1;
    const finalMonthAlreadyIncluded = Math.abs(lastMonthInData - payoffMonths) < 1e-9;
    if (!payoffMonthsIsInteger || !finalMonthAlreadyIncluded) {
      const sEnd = sampleAtMonth(schedule, payoffMonths);
      let finalPrincipal;
      let finalInvestment;
      if (enableInvestment && payoffByInvestment) {
        finalPrincipal = sEnd.investmentValue ?? 0;
        finalInvestment = sEnd.investmentValue ?? 0;
      } else {
        finalPrincipal = 0;
        finalInvestment = sEnd.investmentValue ?? 0;
      }
      const endYear = payoffMonths / 12;
      const lastRowPrincipal = data.length > 0 ? (data[data.length - 1].principal ?? 0) : 0;
      const loanAlreadyPaidOff = lastRowPrincipal <= 0.01 && !(enableInvestment && payoffByInvestment);
      if (enableInvestment && payoffByInvestment) {
        data.push({ year: endYear, principal: finalPrincipal, interest: interestAtEnd ?? sEnd.interestCumulative, investment: finalInvestment });
      } else if (!loanAlreadyPaidOff) {
        data.push({ year: endYear, principal: finalPrincipal, interest: interestAtEnd ?? sEnd.interestCumulative, investment: finalInvestment });
      }
    }
    return data;
  }, [schedule, enableInvestment, payoffByInvestment, payoffMonths, interestAtEnd, enableDelay, delayYears, totalTermYears, effectivePlanYears]);

  return {
    totalDebt,
    setTotalDebt,
    repaymentPlan,
    setRepaymentPlan,
    minPayment,
    setMinPayment,
    monthlyPayment,
    setMonthlyPayment,
    enableGrowth,
    setEnableGrowth,
    growthRate,
    setGrowthRate,
    enableSeparateGrowth,
    setEnableSeparateGrowth,
    investmentGrowthRate,
    setInvestmentGrowthRate,
    enableInvestment,
    monthlyInvestment,
    setMonthlyInvestment,
    enableDelay,
    delayYears,
    setDelayYears,
    delayMonths,
    startingInvestmentCapital,
    setStartingInvestmentCapital,
    planYears,
    effectivePlanYears,
    hasStartedRepaying,
    setHasStartedRepaying,
    yearsAlreadyRepaid,
    setYearsAlreadyRepaid,
    oneTimeRepayments,
    setOneTimeRepayments,
    enableCustomRates,
    setEnableCustomRates,
    customRates,
    setCustomRates,
    defaultRatePct,
    constrainedMonthlyPayment,
    effectiveMinPayment,
    schedule,
    payoffMonths,
    payoffYears,
    payoffRemainderMonths,
    interestAtEnd,
    finalInvestmentValue,
    payoffByInvestment,
    totalCost,
    totalCostWithInvestments,
    totalInvestmentPayments,
    chartData,
    chartDataMonthly,
  };
}
